import { PlanGroupPlanType, PricingPolicyAdjustmentType } from '../../types/api/PlanGroupApiType';
import { SubscriptionApiType } from '../../types/api/SubscriptionApiType';

export type PickSubscription = Pick<
  SubscriptionApiType,
  'billingPolicyInterval' | 'billingPolicyIntervalCount' | 'totalOrderCount' | 'subscriptionLines'
> & {
  /** 契約詳細 API の billingAttempts。渡せるときは渡す。無いときは getNextOrderNumberForPricing が totalOrderCount + 1 だけで次回注文番号を計算する */
  billingAttempts?: SubscriptionApiType['billingAttempts'];
};

/** 次回注文番号を API と決済履歴の両方から見たときの内訳 */
type NextOrderDiagnostics = {
  totalOrderCount: number;
  /** totalOrderCount + 1 */
  fromApi: number;
  billingAttemptsCount: number;
  successfulBillingAttempts: number;
  /** 成功件数 + 1。履歴が無いときは null */
  fromAttempts: number | null;
  /** Math.max(fromAttempts, fromApi) を前提とした採用値 */
  nextOrder: number;
};

/**
 * 次回お届けの「何回目の注文か」（1 始まり）を求める内部処理。
 *
 * - `fromApi` … totalOrderCount + 1
 * - `fromAttempts` … 成功した billingAttempts の件数 + 1（フィルタは Coupon.tsx と同じ）
 *
 * 単独だと片方だけが遅延・欠損し **2 回目のまま** と誤判定されることがあるため、
 * **`Math.max(fromAttempts, fromApi)` を nextOrder とする**（3 回目なのにメタ 99% が残る原因の切り分けで判明）。
 */
function getNextOrderDiagnostics(sub: PickSubscription): NextOrderDiagnostics {
  const fromApi = sub.totalOrderCount + 1;
  const attempts = sub.billingAttempts;

  if (!attempts?.length) {
    return {
      totalOrderCount: sub.totalOrderCount,
      fromApi,
      billingAttemptsCount: 0,
      successfulBillingAttempts: 0,
      fromAttempts: null,
      nextOrder: fromApi,
    };
  }

  const successful = attempts.filter(
    (a) => !a.errorCode && a.errorMessage === ''
  ).length;
  const fromAttempts = successful + 1;

  return {
    totalOrderCount: sub.totalOrderCount,
    fromApi,
    billingAttemptsCount: attempts.length,
    successfulBillingAttempts: successful,
    fromAttempts,
    nextOrder: Math.max(fromAttempts, fromApi),
  };
}

/**
 * 次回の注文番号（1 始まり）。
 * - getSubscriptionPlanPrice（プラン割引の回数判定）
 * - buildSubscribableProduct（2 回目のみ商品メタの HTML を使うか）
 */
export function getNextOrderNumberForPricing(sub: PickSubscription): number {
  return getNextOrderDiagnostics(sub).nextOrder;
}

// ====== 商品の値段計算 ======
export function getSubscriptionPlanPrice(
  subscription: PickSubscription,
  originPrice: number,
  planGroup: PlanGroupPlanType[]
) {
  const { billingPolicyInterval, billingPolicyIntervalCount } = subscription;

  // プランが存在しない場合は除外
  const plan = planGroup.find(
    (p) =>
      p.billingPolicyInterval === billingPolicyInterval &&
      p.billingPolicyIntervalCount === billingPolicyIntervalCount
  );
  if (!plan) return null;

  const nextOrderNumber = getNextOrderNumberForPricing(subscription);
  const discount = calcDiscountPrice(originPrice, plan, nextOrderNumber);
  return {
    currentPriceAmount: discount.discountedPrice,
    sellingPlanId: plan.planId,
    discountPercent: discount.percent,
  };
}

function applyDiscount(price: number, type: PricingPolicyAdjustmentType, value: number) {
  if (type === 'FIXED_AMOUNT') {
    return Math.max(0, price - value);
  }

  // PERCENTAGE
  return Math.round(price - price * (value / 100));
}

/**
 * 割引率の計算
 * @param origin 元の値段
 * @param discounted 割引後の値段
 * @returns 丸めた割引率
 *
 * 一の位が 9 の帯（例: 9～10 未満、19～20 未満）は次の 10% に繰り上げ。
 * それ以外は 5% 刻みに切り捨て（floor）。
 */
export function calcDiscountRate(origin: number, discounted: number) {
  if (origin <= 0) return 0;

  const rawRate = ((origin - discounted) / origin) * 100;

  // 定額引きで実質 1 円など（raw が 99% 台）のとき、下の「9 の帯」繰り上げで 100% になるのを避け 99% 表示にする
  if (discounted > 0 && rawRate >= 99 && rawRate < 100) {
    return 99;
  }

  const k = Math.floor((rawRate - 9) / 10);
  if (k >= 0) {
    const bandLow = 10 * k + 9;
    const bandHigh = 10 * k + 10;
    if (rawRate >= bandLow && rawRate < bandHigh) {
      return 10 * (k + 1);
    }
  }

  return Math.floor(rawRate / 5) * 5;
}

/**
 * 割引価格計算する
 * @param originPrice 元の価格
 * @param plan 該当プラン情報
 * @param count 次回の注文番号（1 始まり）。getSubscriptionPlanPrice が getNextOrderNumberForPricing の結果を渡す
 */
function calcDiscountPrice(originPrice: number, plan: PlanGroupPlanType, count: number) {
  const { pricingPolicyAdjustmentType, pricingPolicyAdjustmentValue, discountTimes } = plan;

  // 初期価格
  let discountedPrice = originPrice;
  let appliedDiscountType = pricingPolicyAdjustmentType;
  let appliedDiscountValue = pricingPolicyAdjustmentValue;

  const baseValue = pricingPolicyAdjustmentValue ?? 0;

  // ===== 1. 基本割引（初回 or 継続） =====
  if (pricingPolicyAdjustmentType) {
    discountedPrice = applyDiscount(discountedPrice, pricingPolicyAdjustmentType, baseValue);
  }

  // ===== 2. 回数指定割引（discountTimes のうち、次回注文番号 count に合うルールのなかで pricingCount が最大のものを採用） =====
  if (discountTimes.length > 0) {
    const candidate = discountTimes
      .filter((d) => d.pricingCount <= count)
      .sort((a, b) => b.pricingCount - a.pricingCount)[0];

    if (candidate) {
      const type = candidate.pricingPolicyAdjustmentType;
      const value = candidate.pricingPolicyAdjustmentValue;

      discountedPrice = applyDiscount(discountedPrice, type, value);
      appliedDiscountType = type;
      appliedDiscountValue = value;
    }
  }

  const percent = calcDiscountRate(originPrice, discountedPrice);

  return {
    discountedPrice,
    type: appliedDiscountType,
    value: appliedDiscountValue,
    percent,
  };
}
